import { db, COLLECTIONS } from '../firebase/db';
import { collection, query, where, orderBy, limit, getDocs, getDoc, doc, Query, CollectionReference } from 'firebase/firestore';
import { DailyUsageSummary, DailyAppUsage, BlockEventData, formatDateKey, parseDateKey } from './types';

// Get usage data for a specific date
export async function getDailyUsage(
  familyId: string,
  deviceId: string,
  date: Date
): Promise<DailyUsageSummary> {
  const dateKey = formatDateKey(date);
  const appsRef = collection(
    db,
    COLLECTIONS.FAMILIES,
    familyId,
    'devices',
    deviceId,
    'dailyUsage',
    dateKey,
    'apps'
  );

  const snapshot = await getDocs(appsRef);
  const apps: DailyAppUsage[] = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      packageName: doc.id,
      appName: data.appName || doc.id,
      duration: data.duration || 0,
      sessions: data.sessions || 0,
      lastUsed: data.lastUsed ? new Date(data.lastUsed) : undefined,
    };
  });

  const totalScreenTime = apps.reduce((sum, app) => sum + app.duration, 0);

  return {
    date,
    dateString: dateKey,
    totalScreenTime,
    apps: apps.sort((a, b) => b.duration - a.duration),
  };
}

// Get usage data for a date range
export async function getUsageDateRange(
  familyId: string,
  deviceId: string,
  startDate: Date,
  endDate: Date
): Promise<DailyUsageSummary[]> {
  const results: DailyUsageSummary[] = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    try {
      const daily = await getDailyUsage(familyId, deviceId, current);
      results.push(daily);
    } catch (err) {
      console.warn(`Failed to load usage for ${formatDateKey(current)}:`, err);
      // Add empty day to keep timeline continuous
      results.push({
        date: new Date(current),
        dateString: formatDateKey(current),
        totalScreenTime: 0,
        apps: [],
      });
    }
    current.setDate(current.getDate() + 1);
  }

  return results;
}

// Get recent block events
export async function getBlockEvents(
  familyId: string,
  deviceId: string,
  limitCount: number = 50
): Promise<BlockEventData[]> {
  const eventsRef = collection(
    db,
    COLLECTIONS.FAMILIES,
    familyId,
    'devices',
    deviceId,
    'events'
  );

  const q = query(
    eventsRef,
    where('type', '==', 'block'),
    orderBy('occurredAt', 'desc'),
    limit(limitCount)
  );

  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        packageName: data.packageName || '',
        appName: data.appName || data.packageName,
        reason: data.reason || 'blocklist',
        timestamp: data.occurredAt ? new Date(data.occurredAt) : new Date(),
        rule: data.rule,
      };
    });
  } catch (err) {
    console.error('Failed to load block events:', err);
    return [];
  }
}

// Get block events for a date range
export async function getBlockEventsForDateRange(
  familyId: string,
  deviceId: string,
  startDate: Date,
  endDate: Date
): Promise<BlockEventData[]> {
  const eventsRef = collection(
    db,
    COLLECTIONS.FAMILIES,
    familyId,
    'devices',
    deviceId,
    'events'
  );

  const q = query(
    eventsRef,
    where('type', '==', 'block'),
    where('occurredAt', '>=', startDate),
    where('occurredAt', '<=', endDate),
    orderBy('occurredAt', 'desc')
  );

  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        packageName: data.packageName || '',
        appName: data.appName || data.packageName,
        reason: data.reason || 'blocklist',
        timestamp: data.occurredAt ? new Date(data.occurredAt) : new Date(),
        rule: data.rule,
      };
    });
  } catch (err) {
    console.error('Failed to load block events for range:', err);
    return [];
  }
}
